import React, { useEffect, useMemo, useState } from 'react';
import {
  getLscAutomationActivation,
  getLscBillingStatus,
  getLscCommissionerDigest,
  getLscLeagueAutomationAccess,
  getLscLeagueSetupProgress,
  getLscSyncStatus,
  listLscVodHandoffSessions,
} from '../api';
import { useLeague } from '../hooks/useLeague';
import { useLeagueReadiness } from '../hooks/useLeagueReadiness';
import { OWNER_ONLY_SETTINGS_COPY } from '../leagueRoles';
import { mapReadinessToCommandCenterChecklist } from '../leagueReadinessMapper';
import type {
  LscAutomationActivationPayload,
  LscBillingStatusResponse,
  LscCommissionerDigest,
  LscLeagueAutomationAccess,
  LscLeagueSetupProgress,
  LscSyncStatus,
  LscVodHandoffSession,
} from '../types';
import { shouldExpandCommissionerCockpit, type PaidBetaReadinessAssessment } from '../paidBetaReadiness';
import AutomationAccessSummary from './AutomationAccessSummary';
import CommandCenterBlockersPanel from './commandCenter/CommandCenterBlockersPanel';
import CommandCenterActivationStatusCard from './commandCenter/CommandCenterActivationStatusCard';
import CommandCenterAutomationBundleCard from './commandCenter/CommandCenterAutomationBundleCard';
import CommandCenterLatestActivityCard from './commandCenter/CommandCenterLatestActivityCard';
import CommandCenterReviewWorkCard from './commandCenter/CommandCenterReviewWorkCard';
import CommandCenterSetupProgressCard from './commandCenter/CommandCenterSetupProgressCard';
import ArchiveAutopublishCommandCenterSummary from './archive/ArchiveAutopublishCommandCenterSummary';
import ErrorBanner from './ErrorBanner';
import LeagueEntitlementSummary from './LeagueEntitlementSummary';
import LscCollapsibleSection from './LscCollapsibleSection';
import PaidBetaReadinessChecklist from './PaidBetaReadinessChecklist';
import PaidBetaSupportCard from './PaidBetaSupportCard';
import VerifiedHandoffAwaitingPanel from './VerifiedHandoffAwaitingPanel';

export interface PaidBetaCommandCenterSectionProps {
  leagueId: string;
  memberCount?: number;
  matchCount?: number;
  archiveCount?: number;
  candidateCount?: number;
  onSetupIncompleteChange?: (incomplete: boolean) => void;
}

function deriveSetupComplete(
  assessment: PaidBetaReadinessAssessment | null,
  setupProgress: LscLeagueSetupProgress | null
): boolean {
  if (assessment?.setupComplete) return true;
  if (setupProgress?.setup_status === 'complete') return true;
  return false;
}

const PaidBetaCommandCenterSection: React.FC<PaidBetaCommandCenterSectionProps> = ({
  leagueId,
  onSetupIncompleteChange,
}) => {
  const { isOwner, isOperator } = useLeague();
  const {
    readiness,
    loading: readinessLoading,
    error: readinessError,
    authError: readinessAuthError,
    reload: reloadReadiness,
  } = useLeagueReadiness(leagueId);

  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [automationAccess, setAutomationAccess] = useState<LscLeagueAutomationAccess | null>(null);
  const [activation, setActivation] = useState<LscAutomationActivationPayload | null>(null);
  const [setupProgress, setSetupProgress] = useState<LscLeagueSetupProgress | null>(null);
  const [digest, setDigest] = useState<LscCommissionerDigest | null>(null);
  const [handoffSessions, setHandoffSessions] = useState<LscVodHandoffSession[]>([]);
  const [syncStatus, setSyncStatus] = useState<LscSyncStatus | null>(null);
  const [supportLoading, setSupportLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setSupportLoading(true);
    (async () => {
      const [
        billing,
        leagueAutomationAccess,
        activationPayload,
        progressPayload,
        digestPayload,
        sessionsPayload,
        syncPayload,
      ] = await Promise.all([
        getLscBillingStatus(leagueId).catch(() => null),
        getLscLeagueAutomationAccess(leagueId).catch(() => null),
        getLscAutomationActivation(leagueId).catch(() => null),
        getLscLeagueSetupProgress(leagueId).catch(() => null),
        getLscCommissionerDigest(leagueId, { sample_limit: 3 }).catch(() => null),
        listLscVodHandoffSessions(leagueId, { limit: 20 }).catch(() => []),
        getLscSyncStatus(leagueId).catch(() => null),
      ]);
      if (cancelled) return;
      setBillingStatus(billing);
      setAutomationAccess(leagueAutomationAccess);
      setActivation(activationPayload);
      setSetupProgress(progressPayload);
      setDigest(digestPayload);
      setHandoffSessions(sessionsPayload);
      setSyncStatus(syncPayload);
      setSupportLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  const loading = readinessLoading || supportLoading;

  const assessment: PaidBetaReadinessAssessment | null = useMemo(
    () => (readiness ? mapReadinessToCommandCenterChecklist(readiness) : null),
    [readiness]
  );

  const setupComplete = deriveSetupComplete(assessment, setupProgress);

  useEffect(() => {
    if (loading || !assessment) return;
    onSetupIncompleteChange?.(shouldExpandCommissionerCockpit(assessment));
  }, [assessment, loading, onSetupIncompleteChange]);

  const showChecklist = assessment?.shouldShowChecklist ?? false;
  const summaryLabel = setupComplete
    ? 'League ready'
    : assessment
      ? assessment.setupComplete
        ? 'League ready'
        : 'Setup needs attention'
      : 'Setup needs attention';

  const reviewCounts = {
    pendingReviewCount: digest?.counts.pending_review ?? 0,
    needsIdentityCount: digest?.counts.needs_identity_attestation ?? 0,
    readyToPublishCount: digest?.counts.ready_to_publish ?? 0,
    unmatchedCount: digest?.counts.synced_unmatched ?? 0,
  };

  return (
    <div className="space-y-6" data-testid="paid-beta-command-center">
      <LeagueEntitlementSummary leagueId={leagueId} billingStatus={billingStatus} loading={loading} />

      {!loading && readinessAuthError === 'unauthorized' ? (
        <ErrorBanner message="Sign in to view league readiness." />
      ) : null}

      {!loading && readinessAuthError === 'forbidden' ? (
        <ErrorBanner message="You do not have permission to view league readiness." />
      ) : null}

      {!loading && !readinessAuthError && (readinessError || !assessment) ? (
        <div className="space-y-3">
          <ErrorBanner
            message={readinessError ?? 'Unable to load setup status. Try again in a moment.'}
          />
          <button type="button" className="btn-secondary text-sm" onClick={reloadReadiness}>
            Retry
          </button>
        </div>
      ) : null}

      {!loading && assessment && !readinessAuthError && !readinessError ? (
        <>
          <CommandCenterBlockersPanel
            leagueId={leagueId}
            readiness={readiness}
            activation={activation}
            billingStatus={billingStatus}
            syncStatus={syncStatus}
            digest={digest}
            handoffSessions={handoffSessions}
            authError={readinessAuthError}
            isOwner={isOwner}
            isOperator={isOperator}
            loading={loading}
          />

          {isOperator ? <ArchiveAutopublishCommandCenterSummary leagueId={leagueId} /> : null}

          <CommandCenterSetupProgressCard
            leagueId={leagueId}
            setupProgress={setupProgress}
            setupComplete={setupComplete}
            loading={supportLoading}
          />

          <CommandCenterActivationStatusCard
            leagueId={leagueId}
            activation={activation}
            loading={supportLoading}
          />

          <CommandCenterAutomationBundleCard activation={activation} loading={supportLoading} />

          <CommandCenterReviewWorkCard leagueId={leagueId} {...reviewCounts} loading={supportLoading} />

          <VerifiedHandoffAwaitingPanel leagueId={leagueId} sessions={handoffSessions} />

          <CommandCenterLatestActivityCard
            leagueId={leagueId}
            digest={digest}
            loading={supportLoading}
          />

          {automationAccess ? (
            <LscCollapsibleSection
              title="Automation lane details"
              subtitle="Advanced lane status for operators and troubleshooting."
              defaultExpanded={false}
              testId="command-center-automation-lane-details"
            >
              <AutomationAccessSummary
                leagueId={leagueId}
                automationAccess={automationAccess}
                billingStatus={billingStatus}
                loading={loading}
                customerFacing
              />
            </LscCollapsibleSection>
          ) : null}

          {showChecklist && !setupComplete ? (
            <PaidBetaReadinessChecklist
              items={assessment.items}
              completedCount={assessment.completedCount}
              totalCount={assessment.totalCount}
              loading={loading}
              setupComplete={setupComplete}
              shouldShowChecklist={showChecklist}
              summaryLabel={summaryLabel}
            />
          ) : null}
        </>
      ) : null}

      {isOwner ? (
        <PaidBetaSupportCard leagueId={leagueId} defaultCollapsed />
      ) : (
        <p className="text-sm text-secondary-500" data-testid="support-diagnostics-owner-only">
          {OWNER_ONLY_SETTINGS_COPY}
        </p>
      )}
    </div>
  );
};

export default PaidBetaCommandCenterSection;
